/**
 * GlobalSearch Component
 * Reusable search component for instruments across Dashboard and DOM pages
 * Features:
 * - Smart search parsing (strike, option type, underlying)
 * - Organized results (EQ → FUT → CE → PE)
 * - Nearest expiry first
 * - Works with Zerodha
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, X, TrendingUp, BarChart3, ArrowUpDown, Plus, Check } from 'lucide-react';
import { globalSearch, formatInstrumentDisplay } from '../services/globalSearchService';
import { apiClient } from '../App';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const GlobalSearch = ({
  onSelect,
  placeholder = "Search: NIFTY, 25600, NIFTY 25600 CE...",
  connectedBroker = 'zerodha',
  className = "",
  showExamples = true,
  allowMultiple = false
}) => {
  const [query, setQuery] = useState('');
  const [addedTokens, setAddedTokens] = useState(new Set()); // Track recently added tokens
  const [results, setResults] = useState({ instruments: [], grouped: {} });
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Pre-load popular instruments for fast local search using dedicated endpoint
  const [popularInstruments, setPopularInstruments] = useState([]);
  const [isPreloading, setIsPreloading] = useState(true);
  
  useEffect(() => {
    const loadPopularInstruments = async () => {
      setIsPreloading(true);
      try {
        // Use dedicated popular instruments endpoint - single fast API call
        const response = await apiClient.get('/instruments/popular');
        if (response.data?.instruments) {
          setPopularInstruments(response.data.instruments);
          console.log(`[GlobalSearch] Loaded ${response.data.total} popular instruments (cached: ${response.data.cached})`);
        }
      } catch (error) {
        console.error('[GlobalSearch] Failed to load popular instruments:', error);
        // Fallback: try a single broad search
        try {
          const fallbackResponse = await apiClient.post('/instruments/search', { query: 'NIFTY' });
          if (fallbackResponse.data?.instruments) {
            setPopularInstruments(fallbackResponse.data.instruments);
            console.log(`[GlobalSearch] Fallback loaded ${fallbackResponse.data.instruments.length} instruments`);
          }
        } catch (fallbackError) {
          console.error('[GlobalSearch] Fallback also failed:', fallbackError);
        }
      } finally {
        setIsPreloading(false);
      }
    };
    
    loadPopularInstruments();
  }, []);

  // Search - combines local preloaded + API search
  const handleSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ instruments: [], grouped: {} });
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);
    
    const query = searchQuery.toUpperCase();

    try {
      // 1. Check preloaded instruments first for instant results
      let localResults = [];
      if (popularInstruments.length > 0) {
        localResults = popularInstruments.filter(inst => 
          inst.tradingsymbol?.toUpperCase().includes(query) ||
          inst.name?.toUpperCase().includes(query)
        );
        if (localResults.length > 0) {
          setResults({ instruments: localResults, grouped: {} });
        }
      }
      
      // 2. Fetch from API for complete results
      const apiResponse = await apiClient.post('/instruments/search', {
        query: searchQuery
      });
      
      console.log('[GlobalSearch] API:', apiResponse.data?.total, 'results');
      
      // 3. Combine local + API results
      const apiInstruments = apiResponse.data?.instruments || [];
      const combined = [...localResults];
      const seenTokens = new Set(localResults.map(i => i.instrument_token));
      
      for (const inst of apiInstruments) {
        if (!seenTokens.has(inst.instrument_token)) {
          combined.push(inst);
        }
      }
      
      setResults({ 
        instruments: combined,
        grouped: apiResponse.data?.grouped || {}
      });
      
    } catch (error) {
      console.error('Search failed:', error);
      // Fallback to local results only
      const localResults = popularInstruments.filter(inst => 
        inst.tradingsymbol?.toUpperCase().includes(query) ||
        inst.name?.toUpperCase().includes(query)
      );
      if (localResults.length > 0) {
        setResults({ instruments: localResults, grouped: {} });
      }
    } finally {
      setIsSearching(false);
    }
  }, [popularInstruments]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (instrument) => {
    const token = instrument.instrument_token;
    onSelect && onSelect(instrument);
    
    // Keep dropdown open for multiple selections - only clear query, don't close
    if (!allowMultiple) {
      setQuery('');
      setShowDropdown(false);
    } else {
      // For multiple selection mode, keep dropdown open with same results
      // Don't clear query - just show feedback
      
      // Mark as added for visual feedback
      setAddedTokens(prev => new Set([...prev, token]));
      
      // Show brief toast feedback
      toast.success(`Added ${instrument.tradingsymbol}`, { duration: 1500 });
      
      // Keep dropdown open - don't change showDropdown state
      // Keep existing search results visible
      
      // Clear the "added" indicator after 3 seconds
      setTimeout(() => {
        setAddedTokens(prev => {
          const newSet = new Set(prev);
          newSet.delete(token);
          return newSet;
        });
      }, 3000);
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'EQ': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'FUT': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      case 'CE': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'PE': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'IDX': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      case 'ETF': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'MCX': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getExchangeColor = (exchange) => {
    switch (exchange) {
      case 'NSE': return 'text-cyan-400';
      case 'BSE': return 'text-pink-400';
      case 'NFO': return 'text-purple-400';
      case 'BFO': return 'text-pink-400';
      case 'MCX': return 'text-orange-400';
      case 'CDS': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  // Categorize instruments for better display
  const categorizeResults = (instruments) => {
    const categories = {
      futures: [],
      equity: [],
      callOptions: [],
      putOptions: [],
      etf: [],
      index: [],
      commodity: []
    };

    instruments.forEach(inst => {
      const type = inst.instrument_type || '';
      const exchange = inst.exchange || '';
      const segment = inst.segment || '';
      
      // Debug log first few instruments
      if (categories.futures.length < 2 && (type === 'FUT' || type.includes('FUT'))) {
        console.log('[categorizeResults] Found FUT:', inst.tradingsymbol, 'type:', type, 'exchange:', exchange);
      }
      
      if (type === 'FUT' || type === 'FUTIDX' || type === 'FUTSTK' || type === 'FUTCOM') {
        if (exchange === 'MCX' || segment?.includes('MCX')) {
          categories.commodity.push(inst);
        } else {
          categories.futures.push(inst);
        }
      } else if (type === 'CE' || type === 'OPTIDX' && inst.tradingsymbol?.includes('CE')) {
        categories.callOptions.push(inst);
      } else if (type === 'PE' || type === 'OPTIDX' && inst.tradingsymbol?.includes('PE')) {
        categories.putOptions.push(inst);
      } else if (type === 'EQ') {
        if (inst.tradingsymbol?.includes('ETF') || inst.tradingsymbol?.includes('GOLD') || inst.tradingsymbol?.includes('NIFTYBEES')) {
          categories.etf.push(inst);
        } else {
          categories.equity.push(inst);
        }
      } else if (segment?.includes('INDICES') || type === 'INDEX') {
        categories.index.push(inst);
      } else {
        categories.equity.push(inst);
      }
    });

    console.log('[categorizeResults] Results:', {
      futures: categories.futures.length,
      commodity: categories.commodity.length,
      equity: categories.equity.length,
      etf: categories.etf.length,
      callOptions: categories.callOptions.length,
      putOptions: categories.putOptions.length
    });

    return categories;
  };

  const renderResultGroup = (title, instruments, icon, maxItems = 10) => {
    if (!instruments || instruments.length === 0) return null;
    
    return (
      <div className="mb-2">
        <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#4a5a7a] font-semibold flex items-center gap-2 bg-[#0a0e17] sticky top-0">
          {icon}
          {title} ({instruments.length})
        </div>
        {instruments.slice(0, maxItems).map((inst, idx) => {
          const display = formatInstrumentDisplay(inst);
          // Show full name if available and different from symbol
          const fullName = inst.name || inst.company || '';
          const showFullName = fullName && fullName !== display.symbol;
          const exchange = inst.exchange || 'NSE';
          return (
            <div
              key={`${display.symbol}-${idx}`}
              onClick={() => handleSelect(inst)}
              className={cn(
                "px-3 py-2 hover:bg-[#1a2540] cursor-pointer flex items-center justify-between group transition-colors",
                addedTokens.has(inst.instrument_token) && "bg-green-900/30 border-l-2 border-green-500"
              )}
            >
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded border font-medium flex-shrink-0",
                    getBadgeColor(display.badge)
                  )}>
                    {display.badge || 'OTH'}
                  </span>
                  <span className="text-sm text-white font-medium truncate">
                    {display.symbol}
                  </span>
                  {display.expiry && (
                    <span className="text-[10px] text-[#6b7a99] flex-shrink-0">
                      {display.expiry}
                    </span>
                  )}
                </div>
                {showFullName && (
                  <span className="text-[10px] text-[#4a5a7a] truncate pl-[42px]">
                    {fullName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={cn("text-[10px] font-medium", getExchangeColor(exchange))}>{exchange}</span>
                {addedTokens.has(inst.instrument_token) ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Plus className="w-4 h-4 text-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const hasResults = results.instruments && results.instruments.length > 0;
  const { grouped } = results;

  return (
    <div className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#4a5a7a]" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder}
          className="w-full bg-[#0a1628] border border-[#1a2540] rounded-lg pl-10 pr-10 py-2.5 text-sm text-white placeholder-[#4a5a7a] focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 outline-none transition-all"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults({ instruments: [], grouped: {} });
              setShowDropdown(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#4a5a7a] hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {isSearching && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Search Examples */}
      {showExamples && !query && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <span className="text-[10px] text-[#4a5a7a]">Try:</span>
          {['NIFTY', '25600', 'BANKNIFTY FUT', '25600 CE'].map(example => (
            <button
              key={example}
              onClick={() => setQuery(example)}
              className="text-[10px] px-2 py-0.5 bg-[#1a2540] hover:bg-[#2a3550] text-cyan-400 rounded transition-colors"
            >
              {example}
            </button>
          ))}
        </div>
      )}

      {/* Results Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-[#0d1520] border border-[#1a2540] rounded-lg shadow-2xl max-h-[400px] overflow-y-auto"
          style={{ boxShadow: '0 10px 50px rgba(0,0,0,0.5)' }}
        >
          {isSearching && !hasResults ? (
            <div className="p-4 text-center text-[#4a5a7a] text-sm">
              Searching...
            </div>
          ) : hasResults ? (
            <>
              {/* Use categorized results for better organization */}
              {(() => {
                const categories = categorizeResults(results.instruments);
                const q = (query || '').trim().toUpperCase();

                // A category "leads" if any instrument's tradingsymbol or name STARTS WITH the query.
                // Lead categories render first so prefix matches (e.g. OLAELEC for "OLA") appear above
                // mere contains-matches (e.g. CHOLAFIN for "OLA").
                const hasPrefixMatch = (arr) => {
                  if (!arr || arr.length === 0 || !q) return false;
                  return arr.some(i => {
                    const sym = (i.tradingsymbol || '').toUpperCase();
                    const nm = (i.name || '').toUpperCase();
                    return sym.startsWith(q) || nm.startsWith(q);
                  });
                };

                const sectionDefs = [
                  { key: 'futures', title: 'Index Futures', items: categories.futures, icon: <ArrowUpDown className="w-3 h-3 text-purple-400" />, max: 8 },
                  { key: 'commodity', title: 'Commodity (MCX)', items: categories.commodity, icon: <TrendingUp className="w-3 h-3 text-orange-400" />, max: 8 },
                  { key: 'equity', title: 'Equity', items: categories.equity, icon: <BarChart3 className="w-3 h-3 text-blue-400" />, max: 8 },
                  { key: 'etf', title: 'ETF', items: categories.etf, icon: <BarChart3 className="w-3 h-3 text-yellow-400" />, max: 5 },
                  { key: 'callOptions', title: 'Options CE', items: categories.callOptions, icon: <span className="text-green-400 text-[10px] font-bold">CE ▲</span>, max: 10 },
                  { key: 'putOptions', title: 'Options PE', items: categories.putOptions, icon: <span className="text-red-400 text-[10px] font-bold">PE ▼</span>, max: 10 },
                  { key: 'index', title: 'Indices', items: categories.index, icon: <TrendingUp className="w-3 h-3 text-cyan-400" />, max: 5 },
                ];

                // Stable partition: prefix-matched categories first, contains-only after.
                const leading = sectionDefs.filter(s => hasPrefixMatch(s.items));
                const trailing = sectionDefs.filter(s => !hasPrefixMatch(s.items));

                // Within each section, also reorder items so prefix matches come first
                const reorderItems = (items) => {
                  if (!q) return items;
                  const prefix = [];
                  const contains = [];
                  items.forEach(i => {
                    const sym = (i.tradingsymbol || '').toUpperCase();
                    const nm = (i.name || '').toUpperCase();
                    if (sym.startsWith(q) || nm.startsWith(q)) prefix.push(i);
                    else contains.push(i);
                  });
                  return [...prefix, ...contains];
                };

                return (
                  <>
                    {[...leading, ...trailing].map(s =>
                      renderResultGroup(s.title, reorderItems(s.items), s.icon, s.max)
                    )}
                  </>
                );
              })()}
              
              {/* Show total count */}
              <div className="px-3 py-2 text-[10px] text-[#4a5a7a] border-t border-[#1a2540] bg-[#0a0e17]">
                Found {results.instruments.length} instruments
              </div>
            </>
          ) : query.length >= 2 ? (
            <div className="p-4 text-center text-[#4a5a7a] text-sm">
              No instruments found for "{query}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
